import {
    CULLFACE_BACK,
    BLENDMODE_ZERO,
    BlendState,
    Entity,
    MeshInstance,
    Quat,
    StandardMaterial,
    Vec3,
    Color,
    createMesh
} from 'playcanvas';

import { Element, ElementType } from './element';
import { defaultGlobalClipBox, GlobalClipBox } from './global-clip-box';

const tmpLocal = new Vec3();
const tmpWorld = new Vec3();
const planeNormal = new Vec3();
const qFace = new Quat();
const qTmp = new Quat();
const vFrom = new Vec3(0, 0, 1);

// Build a quaternion that rotates unit vector `from` to unit vector `to`.
// Handles parallel and opposite vectors.
function quatFromTo(out: Quat, from: Vec3, to: Vec3) {
    const dot = from.dot(to);
    if (dot > 0.999999) {
        out.set(0, 0, 0, 1);
        return out;
    }

    if (dot < -0.999999) {
        // 180 deg: choose an arbitrary orthogonal axis
        // Try X axis, if nearly parallel then use Y.
        const axis = Math.abs(from.x) < 0.9 ? new Vec3(1, 0, 0) : new Vec3(0, 1, 0);
        axis.cross(from, axis).normalize();
        // quat from axis-angle PI
        out.set(axis.x, axis.y, axis.z, 0);
        return out;
    }

    // General case
    const axis = new Vec3();
    axis.cross(from, to);
    const s = Math.sqrt((1 + dot) * 2);
    const invS = 1 / s;
    out.set(axis.x * invS, axis.y * invS, axis.z * invS, s * 0.5);
    return out;
}

// 6 face directions in local box space (normals pointing outward)
const faceNormals = [
    new Vec3(1, 0, 0),   // +X
    new Vec3(-1, 0, 0),  // -X
    new Vec3(0, 1, 0),   // +Y
    new Vec3(0, -1, 0),  // -Y
    new Vec3(0, 0, 1),   // +Z
    new Vec3(0, 0, -1)   // -Z
];

// Face centers as multipliers of halfExtents
const faceOffsets = [
    new Vec3(1, 0, 0),
    new Vec3(-1, 0, 0),
    new Vec3(0, 1, 0),
    new Vec3(0, -1, 0),
    new Vec3(0, 0, 1),
    new Vec3(0, 0, -1)
];

// A single unit plane (in XY), facing +Z.
// We'll position/rotate per-face and scale to halfExtents.
const unitPlaneVerts = [[-1, -1, 0], [1, -1, 0], [1, 1, 0], [-1, 1, 0]];

function createPlaneMesh(app: any) {
    const verts = unitPlaneVerts;
    const positions = new Float32Array([
        verts[0][0], verts[0][1], verts[0][2],
        verts[1][0], verts[1][1], verts[1][2],
        verts[2][0], verts[2][1], verts[2][2],
        verts[0][0], verts[0][1], verts[0][2],
        verts[2][0], verts[2][1], verts[2][2],
        verts[3][0], verts[3][1], verts[3][2]
    ]);

    return createMesh(app.graphicsDevice, positions, {
        normals: null,
        uvs: null,
        indices: null
    });
}

/**
 * Visible wall planes for Clip Mode = Test (ray/AABB).
 *
 * Goal: when camera is OUTSIDE the box, make the *interior-facing* sides of walls opaque.
 * We render only faces whose outward normal points away from the camera (dot < 0).
 */
class TestClipWalls extends Element {
    entities: Entity[] = [];
    material: StandardMaterial;

    constructor() {
        super(ElementType.debug);
    }

    add() {
        const m = new StandardMaterial();
        // Depth-only: do not write any color, only depth.
        // This makes the wall visually invisible, so clipped splat parts show the background.
        m.blendState = new BlendState(
            true,
            undefined,
            BLENDMODE_ZERO,
            BLENDMODE_ZERO,
            undefined,
            BLENDMODE_ZERO,
            BLENDMODE_ZERO
        );
        m.opacity = 0;
        m.depthTest = true;
        // IMPORTANT: write depth so the wall *occludes* splats reliably.
        m.depthWrite = true;
        // No culling so it works regardless of winding.
        (m as any).cull = (window as any)?.pc?.CULLFACE_NONE ?? 0;
        m.update();
        this.material = m;

        const worldLayer = this.scene.app.scene.layers.getLayerByName('World');
        const splatLayer = this.scene.splatLayer;
        for (let i = 0; i < 6; i++) {
            const entity = new Entity(`testClipWall_${i}`);
            const mesh = createPlaneMesh(this.scene.app);

            entity.addComponent('render', {
                meshInstances: [],
                type: 'asset'
            });

            const meshInstance = new MeshInstance(mesh, m);
            entity.render.meshInstances = [meshInstance];

            // IMPORTANT: ensure clip walls don't affect the infinite grid appearance.
            // The grid draws in the Debug layer. These occluder planes should be in World only.
            // Keep draw order before splats but after typical opaque meshes.
            meshInstance.drawOrder = -(500 - i);

            // Be extra explicit about draw layer order.
            // Splats are in World(TRANSP); these should be opaque and go first.
            (meshInstance as any).transparent = false;

            // Render in both World and Splat layers so depth occlusion works for meshes and splats.
            // Keep Debug excluded so these walls do not interfere with grid/debug visuals.
            const layers: number[] = [];
            if (worldLayer) layers.push(worldLayer.id);
            if (splatLayer) layers.push(splatLayer.id);
            if (layers.length > 0) entity.render.layers = layers;

            this.scene.contentRoot.addChild(entity);
            this.entities.push(entity);
        }
    }

    remove() {
        for (const entity of this.entities) {
            // Destroy meshes and materials to prevent GPU memory leaks
            if (entity.render && entity.render.meshInstances) {
                entity.render.meshInstances.forEach((mi: any) => {
                    if (mi.mesh) {
                        mi.mesh.destroy();
                    }
                    if (mi.material) {
                        mi.material.destroy();
                    }
                });
            }
            this.scene.contentRoot.removeChild(entity);
        }
        this.entities = [];
    }

    onPreRender() {
        const events = this.scene.events;
        const clip = (events.invoke('clipBox') ?? defaultGlobalClipBox()) as GlobalClipBox;
        const mode = (events.invoke('clipBox.mode') ?? 'shader') as string;

        const enabled = !!clip?.enabled && mode === 'test';
        if (!enabled) {
            for (const e of this.entities) e.enabled = false;
            return;
        }

        const camPos = this.scene.camera.entity.getPosition();

        // Reuse vectors to avoid per-frame allocations.
        const invRot = new Quat();
        const camLocal = new Vec3();
        const sideVec = new Vec3();
        // IMPORTANT: For correct “section box” behavior when camera is outside,
        // only the faces whose outward normals point *away* from the camera should
        // write depth. Otherwise you end up occluding the interior content and only
        // seeing the excluded outside parts.

        // If the camera is extremely close to a clipping plane, depth-only walls can
        // briefly cause a full-frame reject ("nothing draws") due to precision.
        // This epsilon disables that plane right at the boundary to avoid blank frames.
        const nearPlaneEps = 0.02; // world units

        // If the view ray is nearly parallel to the clipping plane, enabling the plane
        // can create a sudden opaque-looking boundary from outside.
        // Avoid enabling planes when the camera view direction is nearly parallel.
        const grazingDotEps = 0.08; // ~cos(85.4deg)
        const camForward = this.scene.camera.entity.forward.clone().normalize();

        for (let i = 0; i < 6; i++) {
            const entity = this.entities[i];

            // Compute face world normal
            clip.rotation.transformVector(faceNormals[i], planeNormal);

            // Face center
            const offset = faceOffsets[i];
            tmpLocal.set(
                offset.x * clip.halfExtents.x,
                offset.y * clip.halfExtents.y,
                offset.z * clip.halfExtents.z
            );
            clip.rotation.transformVector(tmpLocal, tmpWorld);
            tmpWorld.add(clip.center);

            entity.setPosition(tmpWorld);

            // Rotate plane so its +Z normal points along the face normal.
            // We start from a plane facing +Z and rotate into planeNormal.
            quatFromTo(qFace, vFrom, planeNormal);
            // NOTE: planeNormal is already in WORLD space (clip.rotation applied above),
            // so qFace already represents the final world-space orientation.
            // Multiplying by clip.rotation again double-rotates and can cause faces to
            // “twist” (clockwise/counterclockwise) when viewing them head-on.
            entity.setRotation(qFace);

            // Scale plane to match the face dimensions (no infinite extension)
            const extendFactor = 1;
            const sx = clip.halfExtents.x;
            const sy = clip.halfExtents.y;
            const sz = clip.halfExtents.z;
            // Face is perpendicular to axis of its normal; choose the other two extents.
            if (i < 2) {
                // +/-X face => spans Y,Z
                entity.setLocalScale(sy * extendFactor, sz * extendFactor, 1);
            } else if (i < 4) {
                // +/-Y face => spans X,Z
                entity.setLocalScale(sx * extendFactor, sz * extendFactor, 1);
            } else {
                // +/-Z face => spans X,Y
                entity.setLocalScale(sx * extendFactor, sy * extendFactor, 1);
            }

            // Determine whether camera is inside the box via local space test
            invRot.copy(clip.rotation).invert();
            camLocal.sub2(camPos, clip.center);
            invRot.transformVector(camLocal, camLocal);
            const cameraInside =
                Math.abs(camLocal.x) <= clip.halfExtents.x &&
                Math.abs(camLocal.y) <= clip.halfExtents.y &&
                Math.abs(camLocal.z) <= clip.halfExtents.z;

            // Enable only the faces that are "behind" the box relative to the camera.
            // With planeNormal pointing outward and side = (camPos - faceCenter)·normal:
            // - side < 0: camera is on the interior-facing side of that plane.
            //            This is the correct plane to write depth when you are outside
            //            and want to clip away *outside* splats.
            // - side > 0: camera is on outward side; enabling these would occlude the
            //            interior and make the result look inverted.
            const side = sideVec.sub2(camPos, tmpWorld).dot(planeNormal);
            // If camera is nearly coplanar with the plane, disable it to avoid
            // depth precision artifacts while crossing the wall.
            if (Math.abs(side) < nearPlaneEps) {
                entity.enabled = false;
                continue;
            }

            // If camera view is nearly parallel to the plane, disable the plane to avoid
            // the boundary abruptly appearing opaque while still outside.
            // planeNormal points outward; camForward is where the camera looks.
            // When |dot| is small, we are grazing along the plane.
            const viewDot = Math.abs(planeNormal.dot(camForward));
            if (viewDot < grazingDotEps) {
                entity.enabled = false;
                continue;
            }

            const grazingAngle = Math.abs(side) < 0.15;
            // When inside the clip box, we still want a section-box effect:
            // show the inside region and clip away splats on the far side of each plane.
            // This uses the same half-space rule as the outside case: enable the planes
            // whose outward normals point *away* from the camera (side < 0).
            // (Enabling side > 0 from inside fails to occlude the far-side spill.)
            entity.enabled = side < 0 || grazingAngle;
        }
    }
}

export { TestClipWalls };
